
import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

export default function MortgageRefinance() {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: 'ri-home-heart-line',
      title: 'Homeowner Targeting',
      description: 'Advanced targeting strategies to reach homeowners with equity who are ideal candidates for refinancing.'
    },
    {
      icon: 'ri-calculator-line',
      title: 'Rate Comparison Tools',
      description: 'Interactive calculators and comparison tools that help prospects understand potential savings.'
    },
    {
      icon: 'ri-bank-line',
      title: 'Equity Assessment',
      description: 'Sophisticated lead qualification based on home value appreciation and existing loan terms.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Credit Optimization',
      description: 'Pre-qualification processes that identify borrowers with the best credit profiles for approval.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Market Analysis',
      description: 'We analyze local market conditions, interest rate trends, and homeowner demographics in your target areas.'
    },
    {
      step: '02',
      title: 'Campaign Setup',
      description: 'Create targeted campaigns across Google, Facebook, and other platforms with mortgage-specific messaging.'
    },
    {
      step: '03',
      title: 'Lead Qualification',
      description: 'Advanced qualification process to ensure leads have equity, good credit, and refinancing intent.'
    },
    {
      step: '04',
      title: 'Delivery & Optimization',
      description: 'Real-time lead delivery with continuous optimization based on conversion data and feedback.'
    }
  ];

  const stats = [
    { number: '850K+', label: 'Mortgage Leads Generated' },
    { number: '18%', label: 'Average Conversion Rate' },
    { number: '$285', label: 'Average Cost Per Lead' },
    { number: '72%', label: 'Pre-qualification Rate' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(59, 130, 246, 0.8), rgba(37, 99, 235, 0.8)), url('https://readdy.ai/api/search-image?query=Professional%20mortgage%20specialist%20helping%20happy%20family%20with%20refinancing%20documents%20in%20bright%20modern%20office%2C%20calculator%20and%20house%20model%20on%20desk%2C%20warm%20welcoming%20atmosphere%20with%20natural%20lighting%20showing%20home%20refinancing%20consultation&width=1920&height=800&seq=mortgage-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Mortgage Refinance Lead Generation
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8">
              Generate qualified leads for mortgage refinancing companies targeting homeowners looking to reduce monthly payments, access equity, and secure better rates.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-white text-black hover:bg-gray-100 font-semibold"
              >
                <i className="ri-phone-line mr-2 text-black"></i>
                Get Started Today
              </Button>
              <Button 
                variant="outline"
                size="lg"
                onClick={() => navigate('/case-studies')}
                className="border-white text-white hover:bg-white hover:text-blue-600 font-semibold"
              >
                <i className="ri-bar-chart-line mr-2"></i>
                View Results
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Proven Mortgage Refinance Results
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our specialized approach to mortgage refinance lead generation delivers consistently high-quality prospects.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Our Mortgage Refinance Leads?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We understand the mortgage refinance market and deliver leads that convert into closed loans.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-6">
                  <i className={`${benefit.icon} text-3xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Mortgage Lead Generation Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach designed specifically for mortgage refinance companies to maximize lead quality and conversion rates.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-600 text-white text-2xl font-bold rounded-full mx-auto mb-6">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20mortgage%20office%20with%20loan%20officer%20reviewing%20refinance%20applications%20on%20computer%20screen%2C%20charts%20showing%20interest%20rates%20and%20home%20values%2C%20professional%20financial%20consultation%20environment%20with%20documents%20and%20calculator&width=600&height=400&seq=mortgage-features&orientation=landscape"
                alt="Mortgage Refinance Process"
                className="w-full rounded-2xl shadow-lg object-cover object-top"
              />
            </div>
            <div className="lg:w-1/2">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Advanced Mortgage Targeting
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Our sophisticated targeting identifies homeowners with the highest refinancing potential, focusing on equity levels, current rates, and loan seasoning.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-lg mr-4">
                    <i className="ri-check-line text-blue-600"></i>
                  </div>
                  <span className="text-gray-700">Homeowner equity analysis and targeting</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-lg mr-4">
                    <i className="ri-check-line text-blue-600"></i>
                  </div>
                  <span className="text-gray-700">Current mortgage rate comparison tools</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-lg mr-4">
                    <i className="ri-check-line text-blue-600"></i>
                  </div>
                  <span className="text-gray-700">Credit score pre-qualification</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-lg mr-4">
                    <i className="ri-check-line text-blue-600"></i>
                  </div>
                  <span className="text-gray-700">Real-time lead delivery and tracking</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Generate More Mortgage Refinance Leads?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Let our mortgage lead generation experts help you connect with qualified homeowners ready to refinance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:+910120418794"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-black hover:bg-gray-100 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call +91 0120 418 7944
            </a>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => navigate('/services')}
              className="border-white text-white hover:bg-white hover:text-blue-600"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              All Services
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
